import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import SectionWrapper from '../SectionWrapper';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import {
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from '@chakra-ui/react';
import SwitchField from '../SwitchField';
import { setFormField } from '../../../../store/redux/slices/form.slice';
import { FormState } from '../../../../store/types/form';
import ColorPickerField from '../ColorPickerField';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import FieldEditIndicator from '../FieldEditIndicator';
import { useRef } from 'react';
import { useFieldEditIndicator } from '../hooks/useFieldEditIndicator';
import { useValidateSlug } from '../hooks/useValidateSlug';

const CustomFooter = () => {
  const formEnabled = useAppSelector((s) => s.form.enabled);
  const footerEnabled = useAppSelector((s) => s.form.footerEnabled);
  const footerContent = useAppSelector((s) => s.form.footerContent);
  const footerColor = useAppSelector((s) => s.form.footerColor);
  const templateId = useAppSelector((s) => s.template.uuid);
  const dispatch = useAppDispatch();
  const { handleFocus, validateField, validateSection } =
    useTemplateFieldValidation('form');
  const { validateSlug } = useValidateSlug(templateId);
  const form = useAppSelector((s) => s.form);
  const errors = useAppSelector((s) => s.templateErrors);
  const footerEnabledRef = useRef<HTMLDivElement>(null);
  const footerContentRef = useRef<HTMLDivElement>(null);
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: {
      footerEnabled: footerEnabledRef,
      footerContent: footerContentRef,
    },
  });

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    dispatch(setFormField({ field, value }));
    const updatedForm = { ...form, [field]: value };
    validateField(field, value, { form: updatedForm, validateSlug });

    if (field === 'footerEnabled') {
      validateSection(updatedForm, { validateSlug });
    }
  };

  return (
    <SectionWrapper
      title="Custom footer"
      openOnLoad={true}
      id="custom-footer-section"
    >
      <Flex direction="column" gap={4}>
        <FieldEditIndicator
          fieldRef={footerEnabledRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="50px"
        >
          <FormControl onClick={() => handleFocus('footerEnabled')}>
            <SwitchField
              value={footerEnabled}
              onChange={(value) => {
                handleChange('footerEnabled', value);
              }}
              disabled={!formEnabled}
              label={<FormLabel variant="templateBuilderLabel" mb={0}>Enable footer</FormLabel>}
            />
          </FormControl>
        </FieldEditIndicator>
        <Collapse in={footerEnabled}>
          <Flex direction="column" gap={4}>
            <ColorPickerField
              label="Footer text colour"
              color={footerColor}
              field="footerColor"
              namespace="form"
              isDisabled={!formEnabled}
            />
            <FieldEditIndicator
              fieldRef={footerContentRef}
              isFieldBeingEdited={isFieldBeingEdited}
              activeFieldRef={activeFieldRef}
              left="-40px"
              top="54px"
            >
              <FormControl isInvalid={!!errors['form.footerContent']}>
                <FormLabel variant="templateBuilderLabel">
                  Footer text
                </FormLabel>
                <Input
                  value={footerContent}
                  onChange={(e) =>
                    handleChange('footerContent', e.target.value)
                  }
                  placeholder="Enter your footer text..."
                  onFocus={() => handleFocus('footerContent')}
                  isDisabled={!formEnabled || !footerEnabled}
                />
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {errors['form.footerContent']}
                </FormErrorMessage>
              </FormControl>
            </FieldEditIndicator>
          </Flex>
        </Collapse>
      </Flex>
    </SectionWrapper>
  );
};

export default CustomFooter;
